// Connections — Context Rail module. Two tabs, wall-walled via useScopeData:
//  • Context Graph   = DF-4 GET /context-graphs — the provenance graphs, each EXPANDABLE to its
//    retrieved→cited lineage (merges the former Context + Lineage tabs: one grounding view, not two).
//  • Knowledge Graph = DF-3 GET /knowledge-graph — the canonical KG glance (node/edge counts).
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { GitBranch, GitFork } from "lucide-react";
import { Badge, RightInspectorSection, Tabs, useInspectorFilter } from "@perlab/ui";
import { useScopeData } from "../state/useScopeData";
import { LineageView, SourceArtifactPanel } from "../citation";
import type { SourceLite } from "../citation";
import { useProvenance } from "../citation/useProvenance";
import type { Lineage } from "../citation/useProvenance";

interface ContextGraphItem {
  id: string;
  scope_node_id: string;
  scope_path: string;
  zone: string;
  kind: string;
  label: string | null;
  verdict: string | null;
  confidence: number | null;
  retrieved_chunk_ids: string[];
  cited_chunk_ids: string[];
}

type CView =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: ContextGraphItem[] };



// Knowledge = the canonical KNOWLEDGE GRAPH glance (DF-3), pairing with the Context Graph. A compact
// node/edge type breakdown for this scope (GET /knowledge-graph, wall-filtered). HONEST: DF-3 populates the
// STRUCTURAL graph today (Source/Chunk + BELONGS_TO); the semantic/domain layer is engine-deferred
// (CFR-VW-026) → flagged, never faked. (Keyword chunk SEARCH lives in the top-bar/command-palette, not here.)
interface KgGlanceData {
  total_nodes: number;
  total_edges: number;
  node_types: { type: string; count: number }[];
  edge_types: { type: string; layer: string; count: number }[];
  semantic_layer: "present" | "deferred";
}
type KGView =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; glance: KgGlanceData };

function CountPill({ children }: { children: ReactElement | string | number }): ReactElement {
  return (
    <span className="ml-auto rounded-[2px] bg-[var(--color-neutral-grey-20)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--color-neutral-grey-60)]">
      {children}
    </span>
  );
}

function KnowledgeTab(): ReactElement {
  const { ready, fetchScoped, level } = useScopeData();
  const [view, setView] = useState<KGView>({ kind: "idle" });

  const load = useCallback(async () => {
    if (!ready) {
      setView({ kind: "idle" });
      return;
    }
    setView({ kind: "loading" });
    try {
      const glance = await fetchScoped<KgGlanceData>("/knowledge-graph");
      setView({ kind: "ready", glance });
    } catch (e) {
      setView({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [ready, fetchScoped]);

  useEffect(() => {
    void load();
  }, [load]);

  // Panel filter (audit-03 IR-H1): the inspector's "Filter this panel…" query narrows the KG type lists
  // in place. Hooks are called unconditionally (empty lists until ready) so order is stable across renders.
  const nodeTypes = view.kind === "ready" ? view.glance.node_types : [];
  const edgeTypes = view.kind === "ready" ? view.glance.edge_types : [];
  const { filtered: fNodes, hasQuery, query } = useInspectorFilter(nodeTypes, (n) => n.type);
  const { filtered: fEdges } = useInspectorFilter(edgeTypes, (e) => `${e.type} ${e.layer}`);

  if (view.kind === "idle") {
    return <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">No scope resolved yet.</p>;
  }
  if (view.kind === "loading") {
    return <p role="status" className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">Loading knowledge graph…</p>;
  }
  if (view.kind === "error") {
    return <p role="alert" className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">Couldn’t load: {view.message}</p>;
  }
  const g = view.glance;
  if (g.total_nodes === 0) {
    return <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">No knowledge-graph nodes at this {level ?? "scope"}.</p>;
  }
  return (
    <div className="px-3 py-2">
      <p className="mb-2 font-mono text-xs text-[var(--color-text-secondary)]">
        {g.total_nodes} node{g.total_nodes === 1 ? "" : "s"} · {g.total_edges} edge{g.total_edges === 1 ? "" : "s"}
      </p>
      {hasQuery && fNodes.length === 0 && fEdges.length === 0 && (
        <p className="py-0.5 text-xs text-[var(--color-text-secondary)]">No graph types match “{query}”.</p>
      )}
      {fNodes.length > 0 && (
        <ul className="flex flex-col gap-0.5">
          {fNodes.map((n) => (
            <li key={n.type} className="flex items-center gap-1.5 py-0.5 text-xs text-[var(--color-text-primary)]">
              <GitBranch size={13} className="shrink-0 text-[var(--color-text-secondary)]" aria-hidden />
              <span className="truncate">{n.type}</span>
              <CountPill>{n.count}</CountPill>
            </li>
          ))}
        </ul>
      )}
      {fEdges.length > 0 && (
        <ul className="mt-2 flex flex-col gap-0.5 border-t border-white/[0.08] pt-2">
          {fEdges.map((e) => (
            <li key={`${e.type}:${e.layer}`} className="flex items-center gap-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]">
              <span className="truncate font-mono">{e.type}</span>
              <Badge tone="neutral">{e.layer}</Badge>
              <CountPill>{e.count}</CountPill>
            </li>
          ))}
        </ul>
      )}
      {g.semantic_layer === "deferred" && (
        <p className="mt-2 text-[11px] text-[var(--color-extended-purple-60)]">
          Semantic layer engine-deferred (CFR-VW-026) - structural graph shown.
        </p>
      )}
    </div>
  );
}

// Context Graph = the DF-4 provenance graphs (label · kind · verdict · cited/retrieved), each EXPANDABLE to
// its retrieved→cited lineage via the citation primitives (LineageView → CitationPopover → SourceArtifactPanel,
// real passages from GET /context-graphs/{id}/chunks). Wall-walled via useProvenance. The SINGLE grounding
// view in Connections — the old separate Context list + Lineage tabs are merged here (#4: de-dupe).
function ContextGraphTab(): ReactElement {
  const { ready, fetchScoped, level } = useScopeData();
  const { loadLineage } = useProvenance();
  const [view, setView] = useState<CView>({ kind: "idle" });
  const [openId, setOpenId] = useState<string | null>(null);
  const [lineage, setLineage] = useState<Lineage | null>(null);
  const [lineageErr, setLineageErr] = useState<string | null>(null);
  const [source, setSource] = useState<SourceLite | null>(null);

  const load = useCallback(async () => {
    if (!ready) {
      setView({ kind: "idle" });
      return;
    }
    setView({ kind: "loading" });
    try {
      // D-10: /context-graphs returns the canonical Page envelope — read .items.
      const page = await fetchScoped<{ items: ContextGraphItem[] }>("/context-graphs");
      setView({ kind: "ready", items: page.items });
    } catch (e) {
      setView({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [ready, fetchScoped]);

  useEffect(() => {
    void load();
  }, [load]);

  // Panel filter (audit-03 IR-H1): narrow the provenance graphs in place by label/kind/verdict/zone.
  const items = view.kind === "ready" ? view.items : [];
  const { filtered, hasQuery, query } = useInspectorFilter(
    items,
    (g) => `${g.label ?? ""} ${g.kind} ${g.verdict ?? ""} ${g.zone}`,
  );

  const expand = async (g: ContextGraphItem): Promise<void> => {
    if (openId === g.id) {
      setOpenId(null);
      return;
    }
    setOpenId(g.id);
    setLineage(null);
    setLineageErr(null);
    setSource(null);
    try {
      setLineage(await loadLineage(g));
    } catch (e) {
      setLineageErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div>
      {view.kind === "idle" && (
        <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">No scope resolved yet.</p>
      )}
      {view.kind === "loading" && (
        <p role="status" className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">Loading lineage…</p>
      )}
      {view.kind === "error" && (
        <p role="alert" className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
          Couldn’t load lineage: {view.message}
        </p>
      )}
      {view.kind === "ready" && items.length === 0 && (
        <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
          No context graphs at this {level ?? "scope"}.
        </p>
      )}
      {view.kind === "ready" && items.length > 0 && filtered.length === 0 && hasQuery && (
        <p className="px-3 py-1.5 text-xs text-[var(--color-text-secondary)]">
          No context graphs match “{query}”.
        </p>
      )}
      {view.kind === "ready" && filtered.length > 0 && (
        <ul className="flex flex-col">
          {filtered.map((g) => {
            const open = openId === g.id;
            return (
              <li key={g.id} className="border-b border-[var(--color-border-subtle)] last:border-0">
                <button
                  type="button"
                  onClick={() => void expand(g)}
                  aria-expanded={open}
                  className="flex w-full flex-col px-3 py-1.5 text-left hover:bg-[var(--color-surface-muted)]"
                >
                  <span className="flex items-center gap-1.5">
                    <GitFork size={13} className="shrink-0 text-[var(--color-text-secondary)]" aria-hidden />
                    <span className="truncate text-xs text-[var(--color-text-primary)]" title={g.label ?? g.id}>
                      {g.label ?? "(untitled)"}
                    </span>
                    <Badge tone="neutral">{g.kind}</Badge>
                    {g.verdict && (
                      <Badge tone={g.verdict === "grounded" ? "success" : "warning"}>{g.verdict}</Badge>
                    )}
                  </span>
                  <span className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                    {g.cited_chunk_ids.length}/{g.retrieved_chunk_ids.length} cited · {g.zone}
                  </span>
                </button>
                {open && (
                  <div className="pb-1.5">
                    {lineageErr && (
                      <p role="alert" className="px-3 py-1 text-xs text-[var(--color-text-secondary)]">
                        Couldn’t load lineage: {lineageErr}
                      </p>
                    )}
                    {!lineageErr && !lineage && (
                      <p role="status" className="px-3 py-1 text-xs text-[var(--color-text-secondary)]">Loading lineage…</p>
                    )}
                    {lineage && lineage.binding.id === g.id && (
                      <>
                        <LineageView
                          binding={lineage.binding}
                          citations={lineage.citations}
                          onViewSource={(c) => setSource(c.source ?? null)}
                        />
                        {source && (
                          <div className="px-3 pt-1">
                            <SourceArtifactPanel source={source} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ConnectionsModule(): ReactElement {
  return (
    <RightInspectorSection id="connections" title="Connections" icon={<GitFork size={14} aria-hidden />}>
      <Tabs
        items={[
          { id: "context", label: "Context", content: <ContextGraphTab /> },
          { id: "knowledge", label: "Knowledge", content: <KnowledgeTab /> },
        ]}
        defaultActiveId="context"
      />
    </RightInspectorSection>
  );
}
